import { FC, ReactNode, useContext, useMemo, useState } from 'react';
import {
  faBellConcierge,
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faDice,
  faDog,
  faDumbbell,
  faHotel,
  faSnowflake,
  faSpa,
  faSquareParking,
  faUmbrellaBeach,
  faUtensils,
  faVanShuttle,
  faWaterLadder,
  faWifi,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { FacilityTreeSubgroup, getFacilityTree } from 'services/hotel';
import { FilterModel } from 'ts/hotel/classes/FilterModel';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';
import { getHotelFacilityCodes } from 'utils/hotel';

interface FilterResultFacilitiesProps {
  hotels?: Hotel[];
  onChangeFilter: (filter: FilterModel) => void;
}

interface FacilityFilterItem {
  code: string;
  name: string;
  icon: any;
  displayOrder: number;
}

interface FacilityFilterGroup {
  key: string;
  name: string;
  facilities: FacilityFilterItem[];
}

export const FACILITY_ICONS: Record<number, any> = {
  66: faSnowflake,
  437: faSnowflake,
  117: faSpa,
  22: faSquareParking,
  74: faUtensils,
  19: faWifi,
  37: faDog,
  2: faBellConcierge,
  53: faDumbbell,
  106: faDice,
  29: faVanShuttle,
  283: faUmbrellaBeach,
  290: faUmbrellaBeach,
  92: faWaterLadder,
  93: faWaterLadder,
  94: faWaterLadder,
  95: faWaterLadder,
  96: faWaterLadder,
  97: faWaterLadder,
  98: faWaterLadder,
  99: faWaterLadder,
  363: faWaterLadder,
  364: faWaterLadder,
  365: faWaterLadder,
  411: faWaterLadder,
};

const DEFAULT_FACILITY_ICON = faHotel;
const FACILITY_FILTER_SUBGROUP_LABEL_IDS = new Set([37, 66]);

const getFacilityIcon = (facilityId: number) => FACILITY_ICONS[facilityId] || DEFAULT_FACILITY_ICON;

const FacilityFilterLabel: FC<{
  name: string;
  count: number;
  icon: any;
  trailing?: ReactNode;
}> = ({ name, count, icon, trailing }) => (
  <span className="facility-filter-label">
    <span className="facility-filter-main">
      <span className="facility-filter-icon">
        <FontAwesomeIcon icon={icon || faCircleCheck} />
      </span>
      <Tooltip content={name} position="top">
        <span className="facility-filter-name">{name}</span>
      </Tooltip>
      <span className="facility-filter-count text-light text-xsm"> ({count})</span>
    </span>
    {trailing}
  </span>
);

const FilterResultFacilities: FC<FilterResultFacilitiesProps> = ({ hotels, onChangeFilter }) => {
  const { filter } = useContext(HotelFilterContext);
  const { t } = useTranslation();
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const groups = useMemo<FacilityFilterGroup[]>(() => {
    const tree = getFacilityTree();

    return tree.Tree.flatMap((group) =>
      (group.subgroups || [])
        .map((subgroup: FacilityTreeSubgroup) => {
          const displayed = (subgroup.facilities || [])
            .filter((facility) => facility.display === 1)
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

          const facilities = displayed.map((facility) => ({
            code: facility.facility_code,
            name: facility.name,
            icon: getFacilityIcon(facility.facility_id),
            displayOrder: facility.display_order || 0,
          }));

          const useSubgroupName =
            displayed.length !== 1 ||
            FACILITY_FILTER_SUBGROUP_LABEL_IDS.has(displayed[0].facility_id);

          return {
            key: `${group.group_code}-${subgroup.subgroup_code}`,
            name: useSubgroupName ? subgroup.name : facilities[0].name,
            facilities,
          };
        })
        .filter((subgroup) => !!subgroup.facilities.length),
    ).sort(
      (a, b) => (a.facilities[0]?.displayOrder || 0) - (b.facilities[0]?.displayOrder || 0),
    );
  }, []);

  const { facilityCounts, hotelCodeSets } = useMemo(() => {
    const counts = new Map<string, number>();
    const codeSets = (hotels || []).map((hotel) => getHotelFacilityCodes(hotel));

    codeSets.forEach((hotelCodes) => {
      hotelCodes.forEach((code) => {
        counts.set(code, (counts.get(code) || 0) + 1);
      });
    });

    return { facilityCounts: counts, hotelCodeSets: codeSets };
  }, [hotels]);

  if (!filter || !groups.length) {
    return null;
  }

  const includeList = filter.facilityFilter?.include || [];

  const getFacilityCount = (code: string) => facilityCounts.get(code) || 0;

  const getGroupCount = (codes: string[]) => {
    if (codes.length === 1) {
      return getFacilityCount(codes[0]);
    }

    return hotelCodeSets.filter((hotelCodes) => codes.some((code) => hotelCodes.has(code))).length;
  };

  const handleIncludeChange = (code: string, checked: boolean) => {
    const nextInclude = checked
      ? includeList.includes(code)
        ? includeList
        : [...includeList, code]
      : includeList.filter((value) => value !== code);

    onChangeFilter({
      ...filter,
      facilityFilter: { include: nextInclude },
    });
  };

  const handleGroupChange = (codes: string[], checked: boolean) => {
    const codeSet = new Set(codes);
    const nextInclude = checked
      ? Array.from(new Set([...includeList, ...codes]))
      : includeList.filter((value) => !codeSet.has(value));

    onChangeFilter({
      ...filter,
      facilityFilter: { include: nextInclude },
    });
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((current) =>
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key],
    );
  };

  return (
    <div className="prices-filter mt-10">
      <div className="title-underline text-st mb-10 title-header-grey">{t('hotel.facilities')}</div>
      {groups.map((group, index) => {
        const codes = group.facilities.map((facility) => facility.code);
        const hasMultiple = codes.length > 1;
        const allSelected = codes.every((code) => includeList.includes(code));
        const isExpanded = expandedKeys.includes(group.key);
        const groupCount = getGroupCount(codes);
        const groupDisabled = groupCount === 0 && !allSelected;

        return (
          <div key={group.key} className={index < groups.length - 1 ? 'mb-10' : ''}>
            <div className="h-type-filter text-sm">
              <Checkbox
                className="mb-5 facility-filter-row"
                checked={allSelected}
                disabled={groupDisabled}
                onChange={(checked) => handleGroupChange(codes, checked)}
                htmlLabel={
                  <FacilityFilterLabel
                    name={group.name}
                    count={groupCount}
                    icon={group.facilities[0]?.icon}
                    trailing={
                      hasMultiple ? (
                        <span
                          className="facility-filter-chevron cur-poi"
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            toggleExpanded(group.key);
                          }}
                        >
                          <FontAwesomeIcon icon={isExpanded ? faChevronUp : (faChevronDown as any)} />
                        </span>
                      ) : undefined
                    }
                  />
                }
              />
              {hasMultiple &&
                isExpanded &&
                group.facilities.map((facility) => {
                  const count = getFacilityCount(facility.code);
                  const checked = includeList.includes(facility.code);

                  return (
                    <Checkbox
                      key={facility.code}
                      className="mb-5 ml-10 facility-filter-row"
                      checked={checked}
                      disabled={count === 0 && !checked}
                      onChange={(checkedValue) => handleIncludeChange(facility.code, checkedValue)}
                      htmlLabel={
                        <FacilityFilterLabel
                          name={facility.name}
                          count={count}
                          icon={facility.icon}
                        />
                      }
                    />
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default withErrorBoundary(FilterResultFacilities, 'FilterResultFacilities');
